import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { MatOption } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { MatAccordion } from '@angular/material/expansion';
import { MatFormField, MatLabel } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute, Params } from '@angular/router';
import { Alert, AlertModule, AlertService, AlertType, EmptyStateModule, ScrollbarModule } from '@michelin/theme';
import { Applications } from '../interfaces/application';
import { ConnectionDB } from '../interfaces/connectionDb';
import { Controller } from '../interfaces/controller';
import { Groups } from '../interfaces/groups';
import { ImportFileCsv } from '../interfaces/importFileCsv';
import { ImportFileXlsx } from '../interfaces/importFileXlsx';
import { Script } from '../interfaces/script';
import { TablesVisualization } from '../interfaces/tableVisualization';
import { TablesVisualizationUpdate } from '../interfaces/tableVisualizationUpdate';
import { ApplicationService } from './application.service';
import { ConfigurationDbModalComponent } from './configuration-db-modal/configuration-db-modal.component';
import { ControllerModalComponent } from './controller-modal/controller-modal.component';
import { GroupModalComponent } from './group-modal/group-modal.component';
import { ImportFileModalComponent } from './import-file-modal/import-file-modal.component';
import { ImportFileXlsxModalComponent } from './import-file-xlsx-modal/import-file-xlsx-modal.component';
import { ImportFileXlsxComponent } from './import-file-xlsx/import-file-xlsx.component';
import { ImportFileComponent } from './import-file/import-file.component';
import { RunScriptModalComponent } from './run-script-modal/run-script-modal.component';
import { TableVisualizationModalComponent } from './table-visualization-modal/table-visualization-modal.component';
import { TableVisualizationComponent } from './table-visualization/table-visualization.component';

@Component({
  selector: 'mic-app-application',
  templateUrl: './application.component.html',
  styleUrls: ['./application.component.scss'],
  imports: [
    MatFormField,
    MatLabel,
    MatSelect,
    ScrollbarModule,
    MatOption,
    MatAccordion,
    ImportFileComponent,
    EmptyStateModule,
    ImportFileXlsxComponent,
    TableVisualizationComponent,
    AlertModule
  ]
})
export class ApplicationComponent implements OnInit {
  dialog = inject(MatDialog);
  private readonly applicationService = inject(ApplicationService);
  private readonly alertService = inject(AlertService);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);

  element: Applications;

  functions: ImportFileCsv[] = [];

  xlsx: ImportFileXlsx[] = [];

  xlsxFunctions: ImportFileXlsx[] = [];

  connectionDB: ConnectionDB[] = [];
  controllers: Controller[] = [];
  scripts: Script[] = [];

  groups: Groups[] = [];
  tableVisualizations: TablesVisualization[] = [];

  ngOnInit(): void {
    // Get the application by Name

    this.route.params.subscribe((params: Params) => {
      this.applicationService.getApplicationByName(params['name']).subscribe((response) => {
        this.connectionDB = response.connectionDBS;
        this.controllers = response.controllers;
        this.applicationService.connectionDB = this.connectionDB;

        this.element = response;
        this.tableVisualizations = this.element.tableVisualizations;
        this.functions = this.element.importFileCsvs;
        this.xlsxFunctions = this.element.importFileXlsxes;
        this.scripts = this.element.scripts;
        this.groups = this.element.groups;
        this.cdr.detectChanges();
      });
    });
  }

  /*********************************************************************
   *                                                                   *
   * Display a dialog window for updating a column controller item     *
   * @param item The column item to update                             *
   *                                                                   *
   *********************************************************************/
  onUpdateController(item: Controller) {
    const dialogRef = this.dialog.open(ControllerModalComponent, {
      width: '500px',
      data: item
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.ngOnInit();
        this.businessTask(`The Controller ${item.name} was updated successfully`);
      }
    });
  }

  /*********************************************************************
   *                                                                   *
   * Display a dialog window for updating a database connection item   *
   * @param item The database connection item to update                *
   *                                                                   *
   *********************************************************************/
  onUpdateConnection(item: ConnectionDB) {
    // Open dialog window for updationg connection
    // using ConfigurationDbModalComponent component

    const dialogRef = this.dialog.open(ConfigurationDbModalComponent, {
      width: '500px',
      data: item // add passing in the item data as input
    });

    // Subscribe to the afterClosed() observable method
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        if (item !== null) {
          // If connection item is not nulln reinitialize component
          // and display success message indicating update success
          this.ngOnInit();
          this.businessTask(`The Connection ${item.name} was updated successfully`);
        } else {
          // If connection item is null, reinitialize component
          // and display success message indicating add success
          this.ngOnInit();
          this.businessTask(`The Connection was added successfully`);
        }
      }
    });
  }

  /*********************************************************************
   *                                                                   *
   * Display a dialog window for configuring the Import file csv       *
   * function                                                          *
   *                                                                   *
   *********************************************************************/
  openImportFileCsvConfig(): void {
    const dialogRef = this.dialog.open(ImportFileModalComponent, {
      width: '550px',
      data: null
    });

    dialogRef.afterClosed().subscribe(() => {
      this.ngOnInit(); // TODO update this and add only the one element to the list
    });
  }

  /**********************************************************************
   *                                                                    *
   * Display a dialog window for configuring the Import file Xlsx       *
   * function                                                           *
   *                                                                    *
   *********************************************************************/
  openConfigXlsx(): void {
    const dialogRef = this.dialog.open(ImportFileXlsxModalComponent, {
      width: '600px'
    });
    dialogRef.afterClosed().subscribe(() => {
      this.ngOnInit(); // TODO update this and add only the one element to the list
    });
  }

  /*********************************************************************
   *                                                                   *
   * Display a dialog window for configuring the Script Lunch function *
   * function                                                          *
   *                                                                   *
   *********************************************************************/
  onLunchScript(): void {
    const dialogRef = this.dialog.open(RunScriptModalComponent, {
      width: '500px'
    });
    dialogRef.afterClosed().subscribe(() => {
      this.ngOnInit(); // TODO update this and add only the one element to the list
    });
  }

  /*********************************************************************
   *                                                                   *
   * Display a dialog window for configuring the Grouping              *
   * function                                                          *
   *                                                                   *
   *********************************************************************/
  onConfigGroupingFunction(): void {
    const dialogRef = this.dialog.open(GroupModalComponent, {
      width: '600px',
      data: this.element.id
    });
    dialogRef.afterClosed().subscribe((group: Groups | boolean) => {
      if (group !== undefined && group !== false) {
        this.businessTask(`The Group was added successfully`);
      }
    });
  }

  /*********************************************************************
   *                                                                   *
   * Display a dialog window for configuring the table Visualization   *
   * function                                                          *
   *                                                                   *
   *********************************************************************/
  onOpenTableVisualizationConfig(): void {
    const tableVisualizationUpdate: TablesVisualizationUpdate = {
      tableVisualization: null,
      connectionDBS: this.connectionDB
    };
    const dialogRef = this.dialog.open(TableVisualizationModalComponent, {
      width: '800px',
      data: tableVisualizationUpdate
    });
    dialogRef.afterClosed().subscribe((table: TablesVisualization | boolean) => {
      if (table !== undefined && table !== false) {
        this.tableVisualizations.push(table as TablesVisualization);
        this.businessTask('The table was added successfully');
      }
    });
  }

  updateParentData(tableId: number) {
    const indexTable = this.tableVisualizations.findIndex((table) => table.id === tableId);
    this.removeTable(`the table '${this.tableVisualizations[indexTable].name}' was deleted successfuly`);
    this.tableVisualizations.splice(indexTable, 1);
  }

  businessTask(message: string): void {
    // Do a nusiness task
    this.alertService.showAlert({
      type: AlertType.success,
      content: message
    } as Alert);
  }

  removeTable(message: string): void {
    this.alertService.showAlert({
      type: AlertType.critical,
      content: message
    } as Alert);
  }
}
